import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthStateService } from '@guards/auth-state.service';
import { AppService } from '@services/app.service';
import { AuthService } from '@services/auth.service';
import { DateTime } from 'luxon';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-customer-login-page',
  templateUrl: './customer-login-page.component.html',
  styleUrl: './customer-login-page.component.scss'
})
export class CustomerLoginPageComponent {
  isLogin = true;
  showOtp = false;
  verifiedRegisterOtp = false;

  loginForm!: FormGroup;
  registerForm!: FormGroup;
  otpForm!: FormGroup;
  resendTimer = 30;
  resendDisabled = true;
  private timerInterval: any;

  currentYear = DateTime.now().toFormat('y');

  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    private authState: AuthStateService,
    private router: Router,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private authService: AuthService
  ) {
    this.initForms();
  }

  initForms() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email, this.allowedEmailDomainValidator([
        'gmail.com',
        'yahoo.com',
        'zoho.com'
      ])
      ]]
    });

    this.registerForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email, this.allowedEmailDomainValidator([
        'gmail.com',
        'yahoo.com',
        'zoho.com'
      ])
      ]],
      phoneNumber: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]]
    });

    this.otpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern('^[0-9]{4}$')]]
    });
  }

  startResendTimer() {
    this.resendTimer = 30;
    this.resendDisabled = true;

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.timerInterval = setInterval(() => {
      this.resendTimer--;

      if (this.resendTimer === 0) {
        this.resendDisabled = false;
        clearInterval(this.timerInterval);
      }
    }, 1000);
  }


  /* ========== LOGIN ========== */

  sendLoginOtp() {
    this.spinner.show();
    if (this.loginForm.invalid) return;

    this.appService.loginSendOtp(this.loginForm.value).subscribe({
      next: () => {
        this.showOtp = true;
        this.startResendTimer();
        this.toastr.success('OTP sent to email');
        this.spinner.hide();
      },
      error: err => {
        this.spinner.hide();
        this.toastr.error(err.error?.message || 'OTP failed')
      }
    });
  }

  verifyLoginOtp() {
    this.spinner.show();
    if (this.otpForm.invalid) return;

    const payload = {
      email: this.loginForm.value.email,
      otp: this.otpForm.value.otp
    };

    this.appService.loginVerifyOtp(payload).subscribe({
      next: (res: any) => {
        localStorage.setItem('authToken', res.authHeader);
        this.authState.setUser({ userId: res.login.loginId, userType: res.login.userType });
        this.authService.setSession(res);
        this.spinner.hide();
        this.toastr.success('Login successful');
        this.router.navigate(['/cart']);
      },
      error: (err: any) => {
        this.spinner.hide();
        this.toastr.error(err.error.message || 'Invalid OTP')
      }
    });
  }

  /* ========== REGISTER ========== */

  sendRegisterOtp() {
    this.spinner.show();
    if (this.registerForm.invalid) return;

    this.appService.registrationSendOtp({
      email: this.registerForm.value.email
    }).subscribe({
      next: () => {
        this.showOtp = true;
        this.startResendTimer();
        this.toastr.success('OTP sent to email');
        this.spinner.hide();
      },
      error: (err: any) => {
        this.spinner.hide();
        this.toastr.error(err.error.message || 'OTP failed')
      }
    });
  }

  verifyRegisterOtp() {
    this.spinner.show();
    if (this.otpForm.invalid) return;

    const payload = {
      email: this.registerForm.value.email,
      otp: this.otpForm.value.otp
    };

    this.appService.registrationVerifyOtp(payload).subscribe({
      next: () => {
        this.verifiedRegisterOtp = true;
        // this.showOtp = false;
        this.toastr.success('OTP verified');
        this.saveRegistration();
        // this.spinner.hide();
      },
      error: (err: any) => {
        this.spinner.hide();
        this.toastr.error(err.error.message || 'Invalid OTP')
      }
    });
  }

  saveRegistration() {
    this.spinner.show();
    if (!this.verifiedRegisterOtp) {
      this.toastr.warning('Verify OTP first');
      return;
    }

    const payload = {
            name: this.registerForm.get('firstName').value,
            email: this.registerForm.get('email').value || '',
            userType: 'USER',
            phoneNumber: this.registerForm.get('phoneNumber').value,
            whatsappNumber: this.registerForm.get('phoneNumber').value,
            password: '',
            countryCode: '91',
        };

    this.appService.saveRegistration(payload).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.toastr.success('Registration successful');
        this.authService.setSession(res);
        localStorage.setItem('authToken', res.authHeader);
        this.authState.setUser({ userId: res.login.loginId, userType: res.login.userType });
        this.router.navigate(['/']);
      },
      error: (err: any) => {
        this.spinner.hide();
        this.toastr.error(err.error.message || 'Registration failed')
      }
    });
  }

  resendOtp() {
    if (this.resendDisabled) return;

    this.spinner.show();

    const apiCall = this.isLogin
      ? this.appService.loginSendOtp(this.loginForm.value)
      : this.appService.registrationSendOtp(this.registerForm.value);

    apiCall.subscribe({
      next: () => {
        this.otpForm.reset();
        this.startResendTimer();   // 🔁 RESTART TIMER
        this.toastr.success('OTP resent');
        this.spinner.hide();
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error('Resend failed');
      }
    });
  }


  ngOnDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  /* ========== COMMON ========== */

  toggleMode() {
    this.isLogin = !this.isLogin;
    this.resetOtp();
  }

  resetOtp() {
    this.showOtp = false;
    this.verifiedRegisterOtp = false;
    this.otpForm.reset();
  }

  resetToLogin() {
    this.isLogin = true;
    this.resetOtp();
    this.loginForm.reset();
    this.registerForm.reset();
  }

  allowOnlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  allowedEmailDomainValidator(allowedDomains: string[]) {
    return (control: any) => {
      if (!control.value) return null;

      const email = control.value.toLowerCase();
      const domain = email.substring(email.lastIndexOf('@') + 1);

      return allowedDomains.includes(domain)
        ? null
        : { domainNotAllowed: true };
    };
  }


}
